import React, { useEffect, useState, useCallback } from 'react';
import { EventResponse, VolunteerResponse } from '@codey2/codey-api-types/models';
import ContentContainer from '../../components/common/ContentContainer/ContentContainer';
import Button from '../../components/common/Button/Button';
import { IconButton } from '../../components/common/IconButton/IconButton';
import { DeleteIcon } from '../../components/common/icons/deleteIcon';
import { EditIcon } from '../../components/common/icons/editIcon';
import Modal from '../../components/common/Modal/Modal';
import { EventModal, EventFormData } from '../../components/events/EventModal/EventModal';
import { TextInput } from '../../components/common/TextInput/TextInput';
import { useEventsApi } from '../../app/api/useEventsApi';
import { useVolunteersApi } from '../../app/api/useVolunteersApi';
import { useSnackbar } from '../../navigation/snackbar';
import styles from './EventsPage.module.css';
import { useAuth } from '../../app/auth/AuthContext';

type ModalState = 'closed' | 'create-event' | 'edit-event' | 'create-volunteer';

const formatDateRange = (start: string, end: string): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const dateOptions: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const startFormatted = startDate.toLocaleDateString('nl-NL', dateOptions);
  const startTime = startDate.toLocaleTimeString('nl-NL', timeOptions);
  const endTime = endDate.toLocaleTimeString('nl-NL', timeOptions);
  const endDay = endDate.toLocaleDateString('nl-NL', dateOptions);
  if (startFormatted === endDay) {
    return `${startFormatted} · ${startTime} – ${endTime}`;
  }
  return `${startFormatted} ${startTime} – ${endDay} ${endTime}`;
};

export const EventsPage: React.FC = () => {
  const { getAllEvents, createEvent, deleteEvent, addVolunteersToEvent, removeVolunteersFromEvent } = useEventsApi();
  const { getAllVolunteers, createVolunteer } = useVolunteersApi();
  const { showSnackbar } = useSnackbar();
  const { isAuthenticated } = useAuth();

  const [events, setEvents] = useState<EventResponse[]>([]);
  const [volunteers, setVolunteers] = useState<VolunteerResponse[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('closed');
  const [selectedEvent, setSelectedEvent] = useState<EventResponse | undefined>();
  const [newVolunteerName, setNewVolunteerName] = useState('');

  const loadData = useCallback(async () => {
    setIsLoadingEvents(true);
    try {
      const [fetchedEvents, fetchedVolunteers] = await Promise.all([getAllEvents(), getAllVolunteers()]);
      const sorted = [...fetchedEvents].sort((a, b) => new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime());
      setEvents(sorted);
      setVolunteers(fetchedVolunteers);
    } catch (err) {
      console.log(err);
      showSnackbar('Gegevens konden niet worden geladen', 'error');
    } finally {
      setIsLoadingEvents(false);
    }
  }, [getAllEvents, getAllVolunteers, showSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenCreate = () => {
    setSelectedEvent(undefined);
    setModalState('create-event');
  };

  const handleOpenEdit = (event: EventResponse) => {
    setSelectedEvent(event);
    setModalState('edit-event');
  };

  const handleCloseEventModal = () => {
    setModalState('closed');
    setSelectedEvent(undefined);
  };

  const handleSaveEvent = async (formData: EventFormData, selectedVolunteerIds: number[]) => {
    setIsSaving(true);
    try {
      if (selectedEvent) {
        // Edit mode: sync volunteer assignments
        const currentIds = new Set((selectedEvent.volunteers as VolunteerResponse[]).map(v => v.id));
        const nextIds = new Set(selectedVolunteerIds);

        const toAdd = selectedVolunteerIds.filter(id => !currentIds.has(id));
        const toRemove = Array.from(currentIds).filter(id => !nextIds.has(id));

        await Promise.all([
          ...(toAdd.length > 0 ? [addVolunteersToEvent(selectedEvent.id, toAdd)] : []),
          ...(toRemove.length > 0 ? [removeVolunteersFromEvent(selectedEvent.id, toRemove)] : []),
        ]);
        showSnackbar('Vrijwilligers bijgewerkt', 'success');
      } else {
        // Create mode
        const created = await createEvent({
          title: formData.title,
          description: formData.description,
          startDateTime: formData.startDateTime,
          endDateTime: formData.endDateTime,
        });
        if (selectedVolunteerIds.length > 0) {
          await addVolunteersToEvent(created.id, selectedVolunteerIds);
        }
        showSnackbar('Evenement aangemaakt', 'success');
      }
      await loadData();
      handleCloseEventModal();
    } catch (err) {
      console.log(err);
      showSnackbar('Opslaan mislukt', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEvent = async (event: EventResponse) => {
    if (!window.confirm(`Weet je zeker dat je "${event.title}" wilt verwijderen?`)) return;
    try {
      await deleteEvent(event.id);
      showSnackbar('Evenement verwijderd', 'success');
      await loadData();
    } catch {
      showSnackbar('Verwijderen mislukt', 'error');
    }
  };

  const handleCreateVolunteer = async () => {
    if (!newVolunteerName.trim()) return;
    setIsSaving(true);
    try {
      await createVolunteer({ name: newVolunteerName.trim() });
      showSnackbar('Vrijwilliger aangemaakt', 'success');
      setNewVolunteerName('');
      setModalState('closed');
      await loadData();
    } catch {
      showSnackbar('Vrijwilliger aanmaken mislukt', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const sortedEvents = events;

  return (
    <ContentContainer>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Evenementen</h1>
          {isAuthenticated && (
            <div className={styles.headerActions}>
              <Button type="secondary" onClick={() => setModalState('create-volunteer')}>
                Nieuwe vrijwilliger
              </Button>
              <Button type="primary" onClick={handleOpenCreate}>
                Nieuw evenement
              </Button>
            </div>
          )}
        </div>

        {isLoadingEvents ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Evenementen laden…</p>
          </div>
        ) : sortedEvents.length === 0 ? (
          <div className={styles.emptyState}>
            <p>Er zijn nog geen evenementen. Maak er een aan!</p>
          </div>
        ) : (
          <ul className={styles.eventsList}>
            {sortedEvents.map(event => {
              const eventVolunteers = (event.volunteers as VolunteerResponse[]) ?? [];
              return (
                <li key={event.id} className={styles.eventCard}>
                  <div className={styles.eventCardMain}>
                    <div className={styles.eventCardHeader}>
                      <h2 className={styles.eventTitle}>{event.title}</h2>
                      {isAuthenticated && (
                        <div className={styles.eventActions}>
                          <IconButton icon={EditIcon} onClick={() => handleOpenEdit(event)} />
                          <IconButton icon={DeleteIcon} onClick={() => handleDeleteEvent(event)} />
                        </div>
                      )}
                    </div>

                    <p className={styles.eventDate}>{formatDateRange(event.startDateTime, event.endDateTime)}</p>

                    {event.description && <p className={styles.eventDescription}>{event.description}</p>}

                    <div className={styles.volunteersRow}>
                      {eventVolunteers.length === 0 ? (
                        <span className={styles.noVolunteers}>Geen vrijwilligers ingeschreven</span>
                      ) : (
                        <>
                          <span className={styles.volunteersLabel}>Vrijwilligers:</span>
                          <ul className={styles.volunteerTags}>
                            {eventVolunteers.map(v => (
                              <li key={v.id} className={styles.volunteerTag}>
                                {v.name}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Event create/edit modal */}
      <EventModal
        state={modalState === 'create-event' || modalState === 'edit-event' ? 'open' : 'closed'}
        event={selectedEvent}
        volunteers={volunteers}
        onClose={handleCloseEventModal}
        onSave={handleSaveEvent}
        isSaving={isSaving}
      />

      {/* Add volunteer modal */}
      <Modal
        title="Nieuwe vrijwilliger"
        state={modalState === 'create-volunteer' ? 'open' : 'closed'}
        onClose={() => {
          setModalState('closed');
          setNewVolunteerName('');
        }}
        actions={[
          {
            type: 'secondary',
            label: 'Annuleren',
            onClick: () => {
              setModalState('closed');
              setNewVolunteerName('');
            },
            disabled: isSaving,
          },
          {
            type: 'primary',
            label: isSaving ? 'Opslaan…' : 'Opslaan',
            onClick: handleCreateVolunteer,
            disabled: isSaving || !newVolunteerName.trim(),
          },
        ]}
      >
        <TextInput label="Naam" value={newVolunteerName} onChange={setNewVolunteerName} placeholder="Volledige naam" />
      </Modal>
    </ContentContainer>
  );
};

export default EventsPage;
